import type { MaskBounds } from '@/lib/alphaMask';

/** How much the product may be enlarged beyond the plain fit. */
export const FIT_TO_PRODUCT_MAX_ZOOM = 1.3;
/** The share of the available space the product's box may take up. */
export const FIT_TO_PRODUCT_FILL = 0.9;

export interface ProductFit {
  /** The display factor, plain fit times zoom. */
  scaleFactor: number;
  /** Screen pixels the wrapper moves so the product's box is centred. */
  fitOffset: { x: number; y: number };
}

/**
 * Zooms the plain fit in on the product's own box (from its cutout mask):
 * many templates leave a wide margin around the garment, and on a phone
 * that margin is screen the product could use. The zoom is capped, never
 * below one, and the offset centres the box where the mockup was centred.
 */
export function fitToProduct(input: {
  plainScale: number;
  availableWidth: number;
  availableHeight: number;
  mockupWidth: number;
  mockupHeight: number;
  bounds: MaskBounds | null;
}): ProductFit {
  const {
    plainScale,
    availableWidth,
    availableHeight,
    mockupWidth,
    mockupHeight,
    bounds,
  } = input;
  if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
    return { scaleFactor: plainScale, fitOffset: { x: 0, y: 0 } };
  }
  const boxWidth = bounds.width * mockupWidth * plainScale;
  const boxHeight = bounds.height * mockupHeight * plainScale;
  const zoom = Math.max(
    1,
    Math.min(
      FIT_TO_PRODUCT_MAX_ZOOM,
      (FIT_TO_PRODUCT_FILL * availableWidth) / boxWidth,
      (FIT_TO_PRODUCT_FILL * availableHeight) / boxHeight
    )
  );
  const scaleFactor = plainScale * zoom;
  const centreX = (bounds.x + bounds.width / 2) * mockupWidth;
  const centreY = (bounds.y + bounds.height / 2) * mockupHeight;
  return {
    scaleFactor,
    fitOffset: {
      x: (mockupWidth / 2 - centreX) * scaleFactor,
      y: (mockupHeight / 2 - centreY) * scaleFactor,
    },
  };
}
